import * as React from 'react'
import classNames from 'classnames'
import { Trans } from 'react-i18next'
import i18next from 'i18next'
import { renderCatch } from '../error/renderCatch'
import type { CommonDeps } from '../base/CommonDeps'
import { TimelineEvent as TimelineEventItem } from '../../../shared/webcomponents/react-wrappers/TimelineEvent'
import type { PostEvent } from '../../../shared/domain/postEvents/types.no-deps'
import type { Profile } from '../user/Profile'
import { never } from '../util/never'
import type { UUID } from '../../../shared/lib/typeHelpers.no-deps'
import { type TimelineItemIcon, TimelineItem, type TimelineItemPosition } from '../ui/TimelineItem'
import { actionEventDomId, timelineEventDomId } from './timelineEventDomId'
import { UsersAndTeams } from '../ui/UsersAndTeams'
import { TimelineUser } from '../../../shared/webcomponents/react-wrappers/TimelineUser'

interface Props {
    deps: CommonDeps
    event: PostEvent
    position: TimelineItemPosition
    getProfile: (userId: number) => Profile
    getTeamName: (teamId: number) => string
    onClick?: (event: PostEvent) => void
    routeToUserPage?: (userId: number) => void
}

const Event = (props: {
    eventId: UUID
    position: TimelineItemPosition
    text: React.ReactNode
    icon: TimelineItemIcon
    date: Date
}) => {
    const { eventId, position, text, icon, date } = props
    return (
        <div id={timelineEventDomId(eventId)} className="AppCore__commentTimeline__TimelineEvent">
            <TimelineItem icon={icon} position={position}>
                <div className="AppCore__commentTimeline__TimelineEvent__details">
                    <TimelineEventItem dateTime={new Date(date).toISOString()} iconName={`timeline-${icon}`}>
                        <span slot="text">{text}</span>
                    </TimelineEventItem>
                </div>
            </TimelineItem>
        </div>
    )
}

export class TimelineEvent extends React.Component<Props, {}> {
    public render = () => renderCatch(this.props.deps, this.tryRender)

    private handleEventClick = () => {
        const { event, onClick } = this.props
        if (onClick) onClick(event)
    }

    private handleAuthorClick = (userId: number) => {
        if (!this.props.routeToUserPage) {
            return
        }

        const userProfile = this.props.getProfile(userId)
        if (!userProfile || userProfile.isDeleted) {
            return
        }

        this.props.routeToUserPage(userProfile.userId)
    }

    private tryRender = () => {
        const { event, getProfile, getTeamName, onClick, position } = this.props
        const deletedUserTranslation = i18next.t('appcore:User.unknown')

        const authorProfile = getProfile(event.user)
        const userProfileIsClickable = !!this.props.routeToUserPage && authorProfile && !authorProfile.isDeleted

        const actionEvent = (
            <span
                id={actionEventDomId(event.id)}
                className={classNames({
                    'AppCore__commentTimeline__TimelineEvent--clickable': onClick != null,
                })}
                onClick={this.handleEventClick}
            ></span>
        )

        const author = (
            <TimelineUser
                key={event.user}
                name={authorProfile?.isDeleted ? deletedUserTranslation : authorProfile?.name || deletedUserTranslation}
                type={userProfileIsClickable ? 'link' : 'plain'}
                onClick={() => this.handleAuthorClick(event.user)}
            />
        )

        switch (event.type) {
            case 'actionCompleted':
                return (
                    <Event
                        eventId={event.id}
                        position={position}
                        text={
                            <Trans i18nKey="appcore:TimelineEvent.actionCompleted">
                                {author}
                                {actionEvent}
                            </Trans>
                        }
                        icon="lightning"
                        date={event.at}
                    />
                )

            case 'actionCreated':
                return (
                    <Event
                        eventId={event.id}
                        position={position}
                        text={
                            <Trans i18nKey="appcore:TimelineEvent.actionCreated">
                                {author}
                                {actionEvent}
                                <UsersAndTeams
                                    users={event.assignees}
                                    teams={event.teamAssignees}
                                    getUserProfile={getProfile}
                                    getTeamName={getTeamName}
                                    onUserClick={(userId: number) => this.props.routeToUserPage(userId)}
                                />
                            </Trans>
                        }
                        icon="lightning"
                        date={event.at}
                    />
                )

            case 'actionDeleted':
                return (
                    <Event
                        eventId={event.id}
                        position={position}
                        text={
                            <Trans i18nKey="appcore:TimelineEvent.actionDeleted">
                                {author}
                                {actionEvent}
                            </Trans>
                        }
                        icon="lightning"
                        date={event.at}
                    />
                )

            case 'actionEdited':
                return (
                    <Event
                        eventId={event.id}
                        position={position}
                        text={
                            <Trans i18nKey="appcore:TimelineEvent.actionEdited">
                                {author}
                                {actionEvent}
                            </Trans>
                        }
                        icon="lightning"
                        date={event.at}
                    />
                )

            case 'reportStatusChanged':
                return (
                    <Event
                        eventId={event.id}
                        position={position}
                        text={
                            <Trans i18nKey="appcore:TimelineEvent.reportStatusChanged">
                                {author}
                                <b className="boldText">{{ eventFromName: event.fromName }}</b>
                                <b className="boldText">{{ eventToName: event.toName }}</b>
                            </Trans>
                        }
                        icon="pencil"
                        date={event.at}
                    />
                )

            case 'reportEdited':
                return (
                    <Event
                        eventId={event.id}
                        position={position}
                        text={<Trans i18nKey="appcore:TimelineEvent.reportEdited">{author}</Trans>}
                        icon="pencil"
                        date={event.at}
                    />
                )

            case 'trainingInvalidated':
                return (
                    <Event
                        eventId={event.id}
                        position={position}
                        text={<Trans i18nKey="appcore:TimelineEvent.trainingInvalidated">{author}</Trans>}
                        icon="pencil"
                        date={event.at}
                    />
                )

            case 'trainingManuallyCertified':
                return (
                    <Event
                        eventId={event.id}
                        position={position}
                        text={<Trans i18nKey="appcore:TimelineEvent.trainingManuallyCertified">{author}</Trans>}
                        icon="pencil"
                        date={event.at}
                    />
                )

            case 'reportMarkedAsConfidential':
                return (
                    <Event
                        eventId={event.id}
                        position={position}
                        text={<Trans i18nKey="appcore:TimelineEvent.reportMarkedAsConfidential">{author}</Trans>}
                        icon="pencil"
                        date={event.at}
                    />
                )

            case 'reportUnmarkedAsConfidential':
                return (
                    <Event
                        eventId={event.id}
                        position={position}
                        text={<Trans i18nKey="appcore:TimelineEvent.reportUnmarkedAsConfidential">{author}</Trans>}
                        icon="pencil"
                        date={event.at}
                    />
                )

            default:
                never(event)
        }
    }
}
